import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "children", "edsContext"];
import React from 'react';
import PropTypes from 'prop-types';
import { Collapse } from '@mui/material';
import { default as MuiTreeItem } from '@mui/lab/TreeItem';
import { ChevronDown, ChevronRight, ChevronLeft } from '@ellucian/ds-icons/lib';
import { boxShadowFocus, fontWeightBold, spacing10, spacing20, borderRadiusXSmall } from '../styles/tokens';
import { withStyles } from '../styles';
import { withEDSContext } from './../EDSContext/EDSContext';
var styles = function styles(theme) {
  return {
    root: {
      width: 'fit-content',
      padding: spacing20
      // '&$disabled > $content': {
      //     color: theme.palette.disabled.background,
      //     fill: theme.palette.disabled.background,
      //     pointerEvents: 'none',
      //     boxShadow: 'unset',
      //     background: 'transparent',
      //     padding: 0
      // }
    },
    group: {
      margin: '0 0 0 1.625rem',
      padding: 0
    },
    // style overrides need to be in the content class to override MUI's styles with high specificity
    content: {
      color: theme.palette.grey[600],
      padding: "0 " + spacing10,
      borderRadius: borderRadiusXSmall,
      '&:hover': {
        background: theme.palette.grey[200],
        color: theme.palette.ctaColor.hover,
        fill: theme.palette.ctaColor.hover
      },
      '&:active': {
        color: theme.palette.ctaColor.active,
        fill: theme.palette.ctaColor.active
      },
      // default mui has a background for focus, hover, selected+hover, selected+focused with high specificity
      '&.Mui-focused': {
        background: 'transparent',
        boxShadow: boxShadowFocus
      },
      '& .MuiTreeItem-label': {
        whiteSpace: 'nowrap',
        paddingRight: 0,
        paddingLeft: spacing20,
        '&:hover': {
          background: 'transparent'
        }
      },
      '& .MuiTreeItem-iconContainer': {
        marginRight: spacing20,
        marginLeft: 0,
        width: 'unset'
      },
      '&.Mui-selected': {
        background: 'transparent',
        // an odd behavior occurs here where :hover styles are taking priority over :active, despite them being in the correct order
        '&:hover:not(:active)': {
          background: 'transparent',
          '& .MuiTreeItem-label': {
            color: theme.palette.ctaColor.hover,
            fill: theme.palette.ctaColor.hover
          },
          '& .MuiTreeItem-iconContainer > svg': {
            color: theme.palette.ctaColor.hover,
            fill: theme.palette.ctaColor.hover
          }
        },
        '&:active': {
          background: theme.palette.grey[250],
          '& .MuiTreeItem-label': {
            color: theme.palette.ctaColor.active,
            fill: theme.palette.ctaColor.active
          },
          '& .MuiTreeItem-iconContainer > svg': {
            color: theme.palette.ctaColor.active,
            fill: theme.palette.ctaColor.active
          }
        },
        // Mui-focused class is needed to override MUI's styles but has a higher level of specificity than the other state pseudo-classes
        '&.Mui-focused:not(:active)': {
          background: 'transparent'
        },
        '& .MuiTreeItem-label': {
          color: theme.palette.ctaColor.base,
          fill: theme.palette.ctaColor.base,
          fontWeight: fontWeightBold
        },
        '& .MuiTreeItem-iconContainer > svg': {
          fill: theme.palette.ctaColor.base
        }
      }
      // '&$disabled': {
      //     color: theme.palette.action.disabled,
      //     fill: theme.palette.action.disabled
      // },
    },
    // some selected, focused, iconContainer, label class styles are overridden by MUI's styles with higher specificity
    expanded: {},
    selected: {},
    focused: {},
    // disabled: {}
    iconContainer: {},
    label: {},
    collapseExpandIcons: {
      marginLeft: theme.spacing(1)
    }
  };
};

/**
 * An item in TreeView
 */
var TreeItem = function TreeItem(props) {
  var classes = props.classes,
    children = props.children,
    edsContext = props.edsContext,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var classOverrides = {
    root: classes.root,
    expanded: classes.expanded,
    selected: classes.selected,
    group: classes.group,
    content: classes.content,
    iconContainer: classes.iconContainer,
    label: classes.label,
    focused: classes.focused
  };
  return /*#__PURE__*/React.createElement(MuiTreeItem, _extends({
    classes: classOverrides
  }, rest, {
    expandIcon: edsContext.dir === 'rtl' ? /*#__PURE__*/React.createElement(ChevronLeft, {
      "data-testid": "chevron-left",
      className: classes.collapseExpandIcons
    }) : /*#__PURE__*/React.createElement(ChevronRight, {
      "data-testid": "chevron-right",
      className: classes.collapseExpandIcons
    }),
    collapseIcon: /*#__PURE__*/React.createElement(ChevronDown, {
      className: classes.collapseExpandIcons
    })
  }), children);
};
TreeItem.muiName = 'TreeItem';
TreeItem.propTypes = {
  /**
   * @ignore
   */
  edsContext: PropTypes.object,
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * @ignore
   */
  collapseIcon: PropTypes.node,
  /**
   * @ignore
   * The component used for the content node.
   * **Note:** Needs to be able to hold a ref.
   */
  ContentComponent: PropTypes.object,
  /**
   * @ignore
   * Props applied to the ContentComponent
   */
  ContentProps: PropTypes.object,
  /**
   * @ignore
   * If `true`, the node is disabled.
   */
  disabled: PropTypes.object,
  /**
   * @ignore
   * The icon displayed next to a end node.
   */
  endIcon: PropTypes.node,
  /**
   * @ignore
   * The icon used to expand the node.
   */
  expandIcon: PropTypes.node,
  /**
   * The icon to display next to the tree node's label.
   */
  icon: PropTypes.node,
  /**
   * The tree node label.
   */
  label: PropTypes.node,
  /**
   * The id of the node.
   */
  nodeId: PropTypes.string.isRequired,
  /**
   * @ignore
   * The component used for the transition.
   */
  TransitionComponent: PropTypes.elementType,
  /**
   * @ignore
   * Props applied to the `Transition` element.
   */
  TransitionProps: PropTypes.object
};
TreeItem.defaultProps = {
  TransitionComponent: Collapse
};
export default withEDSContext(withStyles(styles)(TreeItem));